define('presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptsViewModel',[
    'businessServices/authentication/sessionAccountInfo',
    'businessServices/events/eventManager',
    'businessServices/router/router',
    'common/collections/collectionFilter',
    'common/collections/collectionSorter',
    'common/promises/promiseFactory',
    'common/storage/commonState',
    'presentation/common/actionModal/viewModels/actionModalViewModel',
    'presentation/common/actionModal/viewModels/confirmActionViewModel',
    'presentation/common/warningModal/viewModels/liveReceptionistScriptIntegrityWarningModalViewModel',
    'presentation/settings/liveReceptionistScripts/facades/liveReceptionistScriptsFacade',
    'presentation/settings/liveReceptionistScripts/viewModels/deactivateLiveReceptionistScriptModalViewModel',
    'presentation/settings/liveReceptionistScripts/viewModels/reactivateLiveReceptionistScriptModalViewModel',
    'settings/navigationConfiguration'
], function(){
    return function() {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const ConfirmActionViewModelConstructor = require('presentation/common/actionModal/viewModels/confirmActionViewModel');
        const DeactivateScriptModalConstructor = require('presentation/settings/liveReceptionistScripts/viewModels/deactivateLiveReceptionistScriptModalViewModel');
        const ReactivateScriptModalConstructor = require('presentation/settings/liveReceptionistScripts/viewModels/reactivateLiveReceptionistScriptModalViewModel');

        const FilterConstructor = require('common/collections/collectionFilter');
        const SorterConstructor = require('common/collections/collectionSorter');

        /** @type typeof import('constants/scrollEventSources') */
        const ScrollEventSources = require('constants/scrollEventSources');

        const _i18n = require('i18next');
        const _commonState = require('common/storage/commonState');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');
        const _sessionAccountInfo = require('businessServices/authentication/sessionAccountInfo');

        let _disposables = [];
        let _eventManager = null;
        let _facade = null;
        let _getNextBatchCalled = false;
        let _getNextBatchOfLiveReceptionistScriptsEventId = null;

        const CONFIRM_RESPONSE = "yes";
        const ROWS_PER_PAGE = 20;

        let _state = {
            availableLiveReceptionistScripts: ko.observableArray([]),
            filteredLiveReceptionistScripts: [],
            liveReceptionistScriptsShown: 0
        };

        const _showContactSupport = () => {
            _buildSupportModal();
            self.actionModal.showModal();
        };

        const _showIntegrityWarningsLiveReceptionistScript = (liveReceptionistScriptPresentationObject, integrityWarningPresentationObjects) => {
            _buildIntegrityWarningsModal(liveReceptionistScriptPresentationObject);
            self.integrityWarningModal.addIntegrityWarnings(integrityWarningPresentationObjects);
            self.integrityWarningModal.showModal();
        };

        const _showDeactivateLiveReceptionistScript = (liveReceptionistScriptPresentationObject) => {
            const liveReceptionistScriptId = liveReceptionistScriptPresentationObject.liveReceptionistScriptId;
            _buildDeactivateModal(liveReceptionistScriptPresentationObject);
            self.actionModal.showModal()
                .done((result) => {
                    if (result === CONFIRM_RESPONSE) {
                        _facade.deactivateLiveReceptionistScript(liveReceptionistScriptId)
                            .done(() => {
                                _refreshLiveReceptionistScripts();
                            })
                            .fail((error) => {
                                if (error.constraintViolations && error.constraintViolations.length > 0) {
                                    _showIntegrityWarningsLiveReceptionistScript(liveReceptionistScriptPresentationObject, error.constraintViolations);
                                } else {
                                    error.liveReceptionistScriptId = liveReceptionistScriptId;
                                    throw error;
                                }
                            });
                    }
                })
                .fail((error) => {
                    throw error;
                });
        };

        const _updateDisplayedLiveReceptionistScripts = () => {
            let filteredLiveReceptionistScripts;
            let filterContent = self.liveReceptionistScriptFilter();
            if (filterContent !== '') {
                let liveReceptionistScriptsFilter = new FilterConstructor();
                liveReceptionistScriptsFilter.addProperty('liveReceptionistScriptName');
                liveReceptionistScriptsFilter.addProperty('modifiedDateTime.filterValue');
                liveReceptionistScriptsFilter.addProperty('createdDateTime.filterValue');
                liveReceptionistScriptsFilter.addValue(filterContent);
                filteredLiveReceptionistScripts = liveReceptionistScriptsFilter.filter(_state.availableLiveReceptionistScripts());
                _state.filteredLiveReceptionistScripts = filteredLiveReceptionistScripts;
            }
            else {
                filteredLiveReceptionistScripts = _state.availableLiveReceptionistScripts();
                _state.filteredLiveReceptionistScripts = filteredLiveReceptionistScripts;
            }
        };

        const _liveReceptionistScriptFilterOnChange = () =>{
            _updateDisplayedLiveReceptionistScripts();
            self.lastPage = false;
            _state.liveReceptionistScriptsShown = ROWS_PER_PAGE;

            self.displayLiveReceptionistScripts(_state.filteredLiveReceptionistScripts.slice(0, _state.liveReceptionistScriptsShown));
        };

        const _refreshLiveReceptionistScripts = () => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.getAllLiveReceptionistScripts()
                    .done((liveReceptionistScripts) => {
                        _state.availableLiveReceptionistScripts(liveReceptionistScripts);
                        _state.filteredLiveReceptionistScripts = liveReceptionistScripts;
                        _state.liveReceptionistScriptsShown = ROWS_PER_PAGE;
                        _sortDisplayedLiveReceptionistScripts();
                        if (_getNextBatchOfLiveReceptionistScriptsEventId === null) {
                            _getNextBatchOfLiveReceptionistScriptsEventId = _eventManager.subscribeBottomScrollBufferReached(_getNextBatchOfLiveReceptionistScripts);
                        }
                        deferredObject.resolve();
                    });
            });
        };

        const _getNextBatchOfLiveReceptionistScripts = (eventSource) => {
            if (eventSource !== self.scrollEventSource) {
                return;
            }

            if (self.lastPage === false && _getNextBatchCalled === false){
                _getNextBatchCalled = true;
                let additionalLiveReceptionistScriptsShown = _state.liveReceptionistScriptsShown + ROWS_PER_PAGE;
                let additionalLiveReceptionistScripts = _state.filteredLiveReceptionistScripts.slice(_state.liveReceptionistScriptsShown, additionalLiveReceptionistScriptsShown);
                self.displayLiveReceptionistScripts.push.apply(self.displayLiveReceptionistScripts, additionalLiveReceptionistScripts);
                _state.liveReceptionistScriptsShown = additionalLiveReceptionistScriptsShown;
                if(_state.liveReceptionistScriptsShown >= _state.filteredLiveReceptionistScripts.length) {
                    self.lastPage = true;
                }
                _getNextBatchCalled = false;
            }
        };

        const _sortDisplayedLiveReceptionistScripts = () => {
            let sorter = new SorterConstructor();
            if (sorter.setOptions(self.sortOptions(), self.sortType())) {
                sorter.sort(_state.availableLiveReceptionistScripts);
                _updateDisplayedLiveReceptionistScripts();
                self.lastPage = false;
                _state.liveReceptionistScriptsShown = ROWS_PER_PAGE;
                self.displayLiveReceptionistScripts(_state.filteredLiveReceptionistScripts.slice(0, _state.liveReceptionistScriptsShown));
            }
        };


        const _buildSupportModal = () => {
            self.actionModal
                .clearModal()
                .setContentViewModel(ConfirmActionViewModelConstructor, [_i18n.t('liveReceptionistScripts:supportMessage')])
                .setHeaderText({i18n: 'liveReceptionistScripts:addScript'})
                .setShouldDisplayCancelButton(false)
                .setSubmitButtonText({i18n: 'ok'})
                .setShouldScrollIntoView(false)
                .setShouldDisplayDefaultFooterMessage(true);
        };

        const _buildDeactivateModal = (liveReceptionistScriptPresentationObject) => {
            const constructorParams = [liveReceptionistScriptPresentationObject];
            self.actionModal
                .clearModal()
                .setContentViewModel(DeactivateScriptModalConstructor, constructorParams)
                .setHeaderText({i18n: 'deactivateLiveReceptionistScriptModal:deactivateScript'})
                .setSubmitButtonText({i18n: 'deactivate'});
        };

        const _buildIntegrityWarningsModal = (liveReceptionistScriptPresentationObject) => {
            const displayType = _commonState.typeDisplay.liveReceptionistScript;
            self.integrityWarningModal
                .clearModal()
                .setHeading({key: 'deactivate', options: {type: displayType}})
                .setLiveReceptionistScript(liveReceptionistScriptPresentationObject)
                .addText({i18n: {key: 'inUse', options: {type: displayType.toLowerCase()}}})
                .addIntegrityWarningText({i18n: {key: 'conflicts'}});
        };

        const _buildReactivateModal = (liveReceptionistScriptPresentationObject) => {
            const constructorParams = [liveReceptionistScriptPresentationObject];
            self.actionModal
                .clearModal()
                .setContentViewModel(ReactivateScriptModalConstructor, constructorParams)
                .setHeaderText({i18n: `reactivateLiveReceptionistScriptModal:reactivateScript`})
                .setSubmitButtonText({i18n: 'reactivate'});
        };

        self.scrollEventSource = ScrollEventSources.settingsLiveReceptionistScripts;
        self.actionModal = null;
        self.integrityWarningModal = null;
        self.lastPage = false;
        self.isCompositionComplete = ko.observable(false);
        self.displayLiveReceptionistScripts = ko.observableArray();
        self.addLiveReceptionistScriptUrl = _navigationConfiguration.routesById.addLiveReceptionistScript.url;

        self.sortType = ko.observable("liveReceptionistScriptName");
        _disposables.push(self.sortType.subscribe(_sortDisplayedLiveReceptionistScripts));
        self.sortOptions = ko.observableArray([
            {textI18n: "name", iconName: 'sortName', sortPath : "liveReceptionistScriptName", isAscending : true},
            {textI18n: "recentlyAdded", iconName: 'sortRecentlyAdded', sortPath : "createdDateTime.sortValue", isAscending : false},
            {textI18n: "recentlyModified", iconName: 'sortRecentlyModified', sortPath : "updatedDateTime.sortValue", isAscending : false}
        ]);
        self.liveReceptionistScriptFilter = ko.observable('');
        _disposables.push(self.liveReceptionistScriptFilter.subscribe(_liveReceptionistScriptFilterOnChange));

        self.conversionInProgress = false;
        self.showResults = ko.pureComputed(() => self.displayLiveReceptionistScripts().length > 0);
        self.emptyStateContentKey = ko.pureComputed(() => _state.availableLiveReceptionistScripts().length > 0 ?
            "liveReceptionistScripts:noResults" :
            "liveReceptionistScripts:noLiveReceptionistScripts");

        self.isImpersonating = ko.pureComputed(() => {
            return _sessionAccountInfo.isImpersonating();
        });

        self.addLiveReceptionistScript = () => {
          if (self.isImpersonating()) {
            _router.navigate(_navigationConfiguration.routesById.addLiveReceptionistScript.url);
          } else {
              _showContactSupport();
          }
        };

        self.associateLiveReceptionistScript = () => {
            if (self.isImpersonating()) {
                _router.navigate(_navigationConfiguration.routesById.associateLiveReceptionistScript.url);
            } else {
                _showContactSupport();
            }
        };

        self.deactivateScript = (liveReceptionistScriptPresentationObject) => {
            const liveReceptionistScriptId = liveReceptionistScriptPresentationObject.liveReceptionistScriptId;
            return _promiseFactory.deferIndefinitely((deferredObject) => {
                if (liveReceptionistScriptPresentationObject.isPendingStatus()) {
                    deferredObject.resolve();
                } else {
                    _facade.getConstraints(liveReceptionistScriptId)
                        .done((integrityWarningPresentationObjects) => {
                            if (integrityWarningPresentationObjects.length > 0) {
                                _showIntegrityWarningsLiveReceptionistScript(liveReceptionistScriptPresentationObject, integrityWarningPresentationObjects);
                            } else {
                                _showDeactivateLiveReceptionistScript(liveReceptionistScriptPresentationObject);
                            }
                            deferredObject.resolve();
                        });
                }
            });
        };

        self.reactivateScript = (liveReceptionistScriptPresentationObject) => {
            const liveReceptionistScriptId = liveReceptionistScriptPresentationObject.liveReceptionistScriptId;
            return _promiseFactory.deferIndefinitely(deferredObject => {
                _buildReactivateModal(liveReceptionistScriptPresentationObject);
                self.actionModal.showModal()
                    .done((result) => {
                        if (result === CONFIRM_RESPONSE) {
                            _facade.reactivateLiveReceptionistScript(liveReceptionistScriptId)
                                .done(() => {
                                    _refreshLiveReceptionistScripts();
                            });
                        }
                    });
                deferredObject.resolve();
            });
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.detached = () => {
            _disposables.forEach(s => s.dispose);
            _disposables = [];
            _eventManager.unsubscribe(_getNextBatchOfLiveReceptionistScriptsEventId);
            _facade.dispose();
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/settings/liveReceptionistScripts/facades/liveReceptionistScriptsFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ActionModalViewModelConstructor = require('presentation/common/actionModal/viewModels/actionModalViewModel');
            self.actionModal = new ActionModalViewModelConstructor();

            const IntegrityWarningModalViewModelConstructor = require('presentation/common/warningModal/viewModels/liveReceptionistScriptIntegrityWarningModalViewModel');
            self.integrityWarningModal = new IntegrityWarningModalViewModelConstructor();

            _eventManager = require('businessServices/events/eventManager');

            return _initialize();
        };

        const _initialize = () => {
            _refreshLiveReceptionistScripts();
            return _promiseFactory.wait();
        };
    };
});

